import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Alert } from 'reactstrap';
import { IcoSearch, IcoX } from '../bz/icons';
import Loader from '../common/Loader';
import '../bsa/projects/projectsList.css';
import { SECTIONS } from '../super-admin/registry';
import { useSuperAdminData } from '../super-admin/useSuperAdminData';
import { ApplyProvider } from '../super-admin/SuperAdminAtoms';
import AllTenantSettings from '../super-admin/sections/AllTenantSettings';
import GroupedRows from '../super-admin/sections/GroupedRows';
import Overview from '../super-admin/sections/Overview';
import SearchResults from '../super-admin/sections/SearchResults';
import Vendors from '../super-admin/sections/Vendors';

// After "Open" jumps to a row: wait for the section to render before scrolling
// to it, then keep the tint long enough to be seen.
const JUMP_SCROLL_DELAY_MS = 60;
const JUMP_HIGHLIGHT_MS = 1800;

// /super-admin/:section. The section nav lives in the shell sidebar, so the page
// renders only the active section (same rule as PlatformSettings). Hidden workspace:
// reached by URL only, gated by the backend's admin RBAC.
export default function SuperAdmin() {
  const { section } = useParams();
  const [params] = useSearchParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { loading, loadError, items, appVersion, tenant, apply } = useSuperAdminData();
  const [query, setQuery] = useState('');
  const [focusUid, setFocusUid] = useState(null);
  const searchRef = useRef(null);
  // An unknown (or inherited, like "constructor") section id is Overview.
  const sec = SECTIONS.find(s => s.id === section) || SECTIONS[0];
  const own = useMemo(() => items.filter(i => i.section === sec.id), [items, sec.id]);
  const filter = params.get('filter');
  // A search of only spaces is no search: the input keeps what was typed, the page ignores it.
  const q = query.trim();

  useEffect(() => {
    setQuery('');
  }, [location.pathname]);

  // "/" focuses the search, as on Plan's projects list.
  useEffect(() => {
    const onKey = e => {
      if (e.key === '/' && !/INPUT|TEXTAREA|SELECT/.test(document.activeElement?.tagName)) {
        e.preventDefault();
        searchRef.current?.focus();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Scroll a row into view and tint it briefly after "Open" jumps to it.
  useEffect(() => {
    if (!focusUid) return undefined;
    const scroll = setTimeout(() => {
      document.getElementById(`row-${focusUid}`)?.scrollIntoView({ block: 'center', behavior: 'smooth' });
    }, JUMP_SCROLL_DELAY_MS);
    const clear = setTimeout(() => setFocusUid(null), JUMP_HIGHLIGHT_MS);
    return () => {
      clearTimeout(scroll);
      clearTimeout(clear);
    };
  }, [focusUid]);

  const go = sid => navigate(sid === 'overview' ? '/super-admin' : `/super-admin/${sid}`);
  // Leaving the search matters even when the section is the one already showing.
  const open = sid => {
    setQuery('');
    go(sid);
  };
  const jump = item => {
    setFocusUid(item.rowUid || item.uid);
    go(item.section);
  };

  let body;
  if (q) body = <SearchResults items={items} query={q} onOpen={open} onClear={() => setQuery('')} />;
  else if (sec.id === 'overview')
    body = <Overview items={items} tenant={tenant} appVersion={appVersion} onJump={jump} />;
  else if (sec.id === 'vendors' || sec.id === 'odd-vendors')
    body = (
      <Vendors key={`${sec.id}:${filter || ''}`} sectionId={sec.id} items={items} focusUid={focusUid} preset={filter} />
    );
  else if (sec.id === 'advanced') body = <AllTenantSettings items={items} onJump={jump} focusUid={focusUid} />;
  else body = <GroupedRows items={own} focusUid={focusUid} />;

  return (
    <ApplyProvider apply={apply}>
      <div style={{ maxWidth: 1320, margin: '0 auto', display: 'flex', flexDirection: 'column', gap: 24 }}>
        {/* STYLEGUIDE §6 page head */}
        <div className="bz-section-head" style={{ marginBottom: 0 }}>
          <div>
            <div className="bz-kicker" style={{ color: 'var(--bz-orange-3)' }}>
              {q || sec.group === 'Super Admin' ? 'Super Admin' : `Super Admin · ${sec.group}`}
            </div>
            <h1 className="bz-h1" style={{ marginTop: 4 }}>
              {q ? 'Search' : sec.label}
            </h1>
            <div className="bz-muted" style={{ fontSize: 'var(--fs-md)', marginTop: 4 }}>
              {q ? `Results across all Super Admin sections for “${q}”` : sec.blurb}
            </div>
          </div>
          <div className="bz-row" style={{ gap: 8 }}>
            <label className="bz-input pl-search" style={{ width: 300 }}>
              <IcoSearch size={14} />
              <input
                ref={searchRef}
                value={query}
                placeholder="Search all settings…"
                aria-label="Search Super Admin"
                onChange={e => setQuery(e.target.value)}
                onKeyDown={e => e.key === 'Escape' && setQuery('')}
              />
              {query ? (
                <button
                  type="button"
                  className="pl-search__clear"
                  aria-label="Clear search"
                  onClick={() => setQuery('')}
                >
                  <IcoX size={12} />
                </button>
              ) : (
                <span className="pl-kbd">/</span>
              )}
            </label>
          </div>
        </div>
        {loading ? (
          <Loader text="Loading..." />
        ) : (
          <>
            {loadError && (
              <Alert color="danger" className="mb-0" fade={false}>
                {loadError}
              </Alert>
            )}
            {body}
          </>
        )}
      </div>
    </ApplyProvider>
  );
}
